import { useState, useEffect, useRef, useMemo } from "react";
import { format } from "date-fns";
import { Tabs, Tab, Modal, Button, Form } from "react-bootstrap"
import { config } from "./Components/Config";
import { jwtFetch } from "./utils/apiClient";
import Menu from "./Components/Menu";
import Header from "./Components/Header";
import { useNavigate } from "react-router-dom";
import Select, { components } from "react-select";
import CreatableSelect from 'react-select/creatable';
import { v4 as uuidv4 } from 'uuid';
import 'react-quill-new/dist/quill.snow.css';
import ReactQuill from "react-quill-new";
import MagicUrl from 'quill-magic-url';

const Quill = ReactQuill.Quill;
Quill.register('modules/magicUrl', MagicUrl);
const BlockEmbed = Quill.import('blots/block/embed');

const icons = Quill.import('ui/icons');
icons['file'] = '<i class="bi bi-paperclip" style="font-size: 14px; font-weight: bold;"></i>';

class HTML5Video extends BlockEmbed {
    static create(value) {
        let node = super.create();
        node.setAttribute('controls', 'true');
        node.setAttribute('width', '100%');
        node.style.maxWidth = '400px';
        node.style.maxHeight = '300px';
        node.setAttribute('src', value);
        return node;
    }

    static value(node) {
        return node.getAttribute('src');
    }
}
HTML5Video.blotName = 'html5video';
HTML5Video.tagName = 'video';
Quill.register(HTML5Video);

const CcMultiValue = (props) => {
    const { label, email, value } = props.data;
    const displayLabel = (label && label !== email && label !== value) ? label : (email || value);

    return (
        <components.MultiValue {...props}>
            <div className="d-flex align-items-center">
                {props.data.avatar && (
                    <div className="rounded-circle bg-secondary text-white d-flex align-items-center justify-content-center me-1" style={{ width: '16px', height: '16px', overflow: 'hidden' }}>
                        <img src={props.data.avatar} alt="" style={{ width: '100%', height: '100%' }} />
                    </div>
                )}
                <span>{displayLabel}</span>
                {(label && label !== email && label !== value && email) && <span className="text-muted ms-1 small"> - {email}</span>}
            </div>
        </components.MultiValue>
    );
};

const CcOption = (props) => {
    return (
        <components.Option {...props}>
            <div className="d-flex align-items-center">
                <div className="rounded-circle bg-secondary text-white d-flex align-items-center justify-content-center me-2" style={{ width: '32px', height: '32px', overflow: 'hidden' }}>
                    {props.data.avatar ? <img src={props.data.avatar} alt="" style={{ width: '100%', height: '100%' }} /> : (props.data.label ? props.data.label.charAt(0).toUpperCase() : 'U')}
                </div>
                <div>
                    <div className="fw-bold text-dark" style={{ fontSize: '14px' }}>{props.data.label}</div>
                    <div className="text-muted small" style={{ fontSize: '12px' }}>{props.data.email || props.data.value}</div>
                </div>
            </div>
        </components.Option>
    );
};

const SUPPORT_TAB_STORAGE_KEY = "supportTicketStatus";
const VALID_TICKET_TABS = new Set(["myTicket", "openTicket", "pending", "closed", "watchList", "unassigned"]);

const getSavedTicketTab = () => {
    const saved = localStorage.getItem(SUPPORT_TAB_STORAGE_KEY);
    if (saved && VALID_TICKET_TABS.has(saved)) {
        return saved;
    }
    return "myTicket";
};

const Ticket = () => {
    const [invoiceRequests, setInvoiceRequests] = useState();
    const [tableFilters, setTableFilters] = useState(() => ({ ticketStatus: getSavedTicketTab() }));
    const [tableSearch, setTableSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [isLoadingPage, setIsLoadingPage] = useState(true);
    const [openTicketCount, setOpenTicketCount] = useState(0);
    const [pendingTicketCount, setPendingTicketCount] = useState(0);
    const [closedTicketCount, setClosedTicketCount] = useState(0);
    const [myTicketCount, setMyTicketCount] = useState(0);
    const [watchListCount, setWatchListCount] = useState(0);
    const [unassignedCount, setUnassignedCount] = useState(0);
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 1
    });
    const searchInputRef = useRef(null);
    const navigate = useNavigate();
    const crmUser = JSON.parse(localStorage.getItem('crmUser'));
    // Create Ticket States
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showCreateConfirmModal, setShowCreateConfirmModal] = useState(false);
    const [createFormData, setCreateFormData] = useState({
        name: '',
        to: '',
        subject: '',
        description: ''
    });
    const [isCreating, setIsCreating] = useState(false);
    const [attachments, setAttachments] = useState([]);
    const quillRef = useRef(null);
    const [draftTicketId, setDraftTicketId] = useState(null);
    const [conversationId, setConversationId] = useState(null);
    const [formErrors, setFormErrors] = useState({});

    // Sorting State
    const [sortConfig, setSortConfig] = useState({ key: 'updatedAt', direction: 'desc' });

    // CC States
    const [showCC, setShowCC] = useState(false);
    const [ccValues, setCcValues] = useState([]);
    const [ccOptions, setCcOptions] = useState([]);
    const [ccInputValue, setCcInputValue] = useState("");
    // Filter dropdown options
    const statusOptions = [
        { label: 'All Status', value: '' },
        { label: 'New', value: 'NEW' },
        { label: 'In Progress', value: 'IN_PROGRESS' },
        { label: 'On Hold', value: 'ON_HOLD' },
        { label: 'Information Requested', value: 'INFORMATION_REQUESTED' },
        { label: 'Solved', value: 'SOLVED' },
        { label: 'Closed', value: 'CLOSED' }
    ];

    const [agentOptions, setAgentOptions] = useState([{ label: 'All Agents', value: 'ALL' }]);
    const [selectedStatusFilter, setSelectedStatusFilter] = useState(statusOptions[0]);
    const [selectedAgentFilter, setSelectedAgentFilter] = useState(agentOptions[0]);

    const handleCcKeyDown = (event) => {
        if (!ccInputValue) return;
        switch (event.key) {
            case 'Enter':
            case 'Tab':
            case ',':
                // Check if input contains commas (handling paste or multiple emails)
                if (ccInputValue.includes(',')) {
                    const emails = ccInputValue.split(',').map(e => e.trim()).filter(e => e);
                    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                    const newOptions = [];

                    emails.forEach(email => {
                        if (emailRegex.test(email)) {
                            // Avoid duplicates
                            const exists = ccValues.some(c => c.value === email || c.email === email);
                            if (!exists) {
                                newOptions.push({ label: email, value: email, email: email, __isNew__: true });
                            }
                        }
                    });

                    if (newOptions.length > 0) {
                        event.preventDefault();
                        setCcValues(prev => [...prev, ...newOptions]);
                        setCcInputValue("");
                    }
                } else {
                    const val = ccInputValue.replace(',', '').trim();
                    // Simple email validation
                    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

                    if (val && emailRegex.test(val)) {
                        event.preventDefault();

                        // Avoid duplicates
                        const exists = ccValues.some(c => c.value === val || c.email === val);
                        if (!exists) {
                            const newOption = { label: val, value: val, email: val, __isNew__: true };
                            setCcValues(prev => [...prev, newOption]);
                        }
                        setCcInputValue("");
                    }
                }
                break;
        }
    };

    const handleCcInputChange = (newValue, actionMeta) => {
        // If the user pastes text containing commas
        if (newValue.includes(',')) {
            const emails = newValue.split(',').map(e => e.trim()).filter(e => e);
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            const newOptions = [];

            emails.forEach(email => {
                if (emailRegex.test(email)) {
                    // Avoid duplicates
                    const exists = ccValues.some(c => c.value === email || c.email === email);
                    // Check if already in newOptions to avoid dupes in same paste
                    const inNew = newOptions.some(c => c.value === email);
                    if (!exists && !inNew) {
                        newOptions.push({ label: email, value: email, email: email, __isNew__: true });
                    }
                }
            });

            if (newOptions.length > 0) {
                setCcValues(prev => [...prev, ...newOptions]);
                setCcInputValue("");
                return;
            }
        }
        setCcInputValue(newValue);
    };

    useEffect(() => {
        fetchAgents();
    }, []);

    const fetchAgents = async () => {
        try {
            const response = await jwtFetch(`${config.ENDPOINT}get/user`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ user_department: 'Support', limit: 1000 })
            });
            const json = await response.json();
            if (json.status === 200) {
                const agents = json.data.map(u => ({
                    value: u.user_keyid,
                    label: u.full_name,
                    email: u.email_address,
                    avatar: u.profile_picture
                }));
                const optionsWithUnassigned = [{ value: '', label: 'Unassigned', email: null }, ...agents];
                setCcOptions(agents);
                setAgentOptions([{ label: 'All Agents', value: 'ALL' }, ...optionsWithUnassigned]);
            }
        } catch (error) {
            console.error("Error fetching agents:", error);
        }
    };

    const uploadFileToServer = async (file) => {
        if (!file) return null;

        const formData = new FormData();
        formData.append('file', file);

        try {
            const response = await fetch(`${config.SUPPORT_ENDPOINT}upload`, {
                method: 'POST',
                body: formData
            });

            if (!response.ok) {
                throw new Error('Upload failed');
            }
            const data = await response.json();
            const storedFileName = data?.url ? data.url.split('/').filter(Boolean).pop() : data.fileName;
            const fileUrl = config.SUPPORT_BASEURL + data.url || (data.fileName ? `${config.SUPPORT_ENDPOINT}uploads/${data.fileName}` : null);

            if (fileUrl && storedFileName) {
                return {
                    name: file.name,
                    fileName: storedFileName,
                    url: fileUrl,
                    type: file.type || 'application/octet-stream'
                };
            }
        } catch (error) {
            console.error("Error uploading file:", error);
            alert(`Error uploading ${file.name}`);
        }
        return null;
    };

    // [REFACTORED] Insert Image to Editor
    const insertImageToEditor = (fileData) => {
        if (!fileData || !quillRef.current) return;
        const quill = quillRef.current.getEditor();
        const range = quill.getSelection(true) || { index: quill.getLength() };

        quill.insertEmbed(range.index, 'image', fileData.url);
        quill.setSelection(range.index + 1); // Move cursor after image

        setAttachments(prev => [...prev, fileData]);
    };

    const uploadImage = async (file) => {
        const fileData = await uploadFileToServer(file);
        if (fileData) {
            insertImageToEditor(fileData);
        }
    };

    const imageHandler = () => {
        const input = document.createElement('input');
        input.setAttribute('type', 'file');
        input.setAttribute('accept', 'image/*');
        input.setAttribute('multiple', 'true');
        input.click();

        input.onchange = async () => {
            const files = Array.from(input.files);
            // Upload sequentially to maintain order
            for (const file of files) {
                await uploadImage(file);
            }
        };
    };

    // [FIX] Handle Paste, Drop, and Base64 Images for Create Ticket Modal
    useEffect(() => {
        if (!showCreateModal) return;

        let intervalId;
        let observer;

        const handleBase64Image = async (imgNode) => {
            // Avoid double processing
            if (imgNode.dataset.processing) return;
            imgNode.dataset.processing = "true";

            // Show loading state
            imgNode.style.opacity = '0.5';
            imgNode.style.border = '2px solid orange';

            try {
                // Convert base64 to File
                const res = await jwtFetch(imgNode.src);
                const blob = await res.blob();
                const file = new File([blob], "pasted-image.png", { type: blob.type });

                const fileData = await uploadFileToServer(file);
                if (fileData) {
                    imgNode.src = fileData.url;
                    imgNode.style.opacity = '1';
                    imgNode.style.border = 'none';

                    // Add to attachments state (but do not insert again)
                    setAttachments(prev => [...prev, fileData]);
                } else {
                    console.error("Upload failed");
                    imgNode.style.border = '2px solid red';
                }
            } catch (err) {
                console.error("Failed to upload base64 image", err);
                imgNode.style.border = '2px solid red';
            } finally {
                delete imgNode.dataset.processing;
            }
        };

        const attachListeners = () => {
            if (quillRef.current) {
                const quill = quillRef.current.getEditor();

                // 1. Attach Event Listeners (Capture Phase)
                quill.root.addEventListener('paste', handlePaste, true);
                quill.root.addEventListener('drop', handleDrop, true);
                quill.root.addEventListener('dragover', handleDragOver, true);

                // 2. Attach MutationObserver as Fallback for Base64 Images
                observer = new MutationObserver((mutations) => {
                    mutations.forEach((mutation) => {
                        if (mutation.type === 'childList') {
                            mutation.addedNodes.forEach((node) => {
                                if (node.nodeName === 'IMG' && node.src && node.src.startsWith('data:')) {
                                    handleBase64Image(node);
                                } else if (node.querySelectorAll) {
                                    const images = node.querySelectorAll('img[src^="data:"]');
                                    images.forEach(img => handleBase64Image(img));
                                }
                            });
                        } else if (mutation.type === 'attributes' && mutation.attributeName === 'src') {
                            if (mutation.target.nodeName === 'IMG' && mutation.target.src && mutation.target.src.startsWith('data:')) {
                                handleBase64Image(mutation.target);
                            }
                        }
                    });
                });

                observer.observe(quill.root, { childList: true, subtree: true, attributes: true, attributeFilter: ['src'] });

                clearInterval(intervalId);
            }
        };

        const handlePaste = async (e) => {
            const clipboardData = e.clipboardData || window.clipboardData;
            const items = clipboardData.items;
            const filesToUpload = [];

            // Prevent default paste if we find images
            let hasImage = false;
            for (let i = 0; i < items.length; i++) {
                if (items[i].type.indexOf('image') !== -1) {
                    hasImage = true;
                    const file = items[i].getAsFile();
                    if (file) filesToUpload.push(file);
                }
            }

            if (hasImage) {
                e.preventDefault();
                e.stopPropagation(); // Stop bubbling

                // Upload Sequentially
                for (const file of filesToUpload) {
                    await uploadImage(file);
                }
            }
        };

        const handleDrop = async (e) => {
            e.preventDefault();
            e.stopPropagation();

            const filesToUpload = [];
            const items = e.dataTransfer.items;

            // Priority: Check items (DataTransferItemList)
            if (items) {
                for (let i = 0; i < items.length; i++) {
                    if (items[i].kind === 'file' && items[i].type.startsWith('image/')) {
                        const file = items[i].getAsFile();
                        if (file) filesToUpload.push(file);
                    }
                }
            }

            // Fallback: Check files (FileList) if items didn't yield results or unavailable
            if (filesToUpload.length === 0 && e.dataTransfer.files && e.dataTransfer.files.length > 0) {
                for (let i = 0; i < e.dataTransfer.files.length; i++) {
                    const file = e.dataTransfer.files[i];
                    if (file.type.startsWith('image/')) {
                        filesToUpload.push(file);
                    }
                }
            }

            // Upload Sequentially
            for (const file of filesToUpload) {
                await uploadImage(file);
            }
        };

        const handleDragOver = (e) => {
            e.preventDefault();
            e.stopPropagation();
            e.dataTransfer.dropEffect = 'copy';
        };

        // Try immediately
        attachListeners();

        // If not found, poll
        if (!quillRef.current) {
            intervalId = setInterval(attachListeners, 200);
        }

        return () => {
            if (intervalId) clearInterval(intervalId);
            if (observer) observer.disconnect();
            if (quillRef.current) {
                try {
                    const quill = quillRef.current.getEditor();
                    quill.root.removeEventListener('paste', handlePaste, true);
                    quill.root.removeEventListener('drop', handleDrop, true);
                    quill.root.removeEventListener('dragover', handleDragOver, true);
                } catch (e) {
                    // Ignore error if quill instance is gone
                }
            }
        };
    }, [showCreateModal]);

    const videoHandler = () => {
        const input = document.createElement('input');
        input.setAttribute('type', 'file');
        input.setAttribute('accept', 'video/*');
        input.click();
        input.onchange = async () => {
            const file = input.files[0];
            if (!file) return;
            const formData = new FormData();
            formData.append('file', file);
            try {
                const response = await fetch(`${config.SUPPORT_ENDPOINT}upload`, { method: 'POST', body: formData });
                if (!response.ok) throw new Error('Upload failed');
                const data = await response.json();
                const videoUrl = config.SUPPORT_BASEURL + data.url || (data.fileName ? `${config.SUPPORT_ENDPOINT}uploads/${data.fileName}` : null);
                const storedFileName = data?.url ? data.url.split('/').filter(Boolean).pop() : data.fileName;

                if (videoUrl && storedFileName) {
                    const quill = quillRef.current.getEditor();
                    const range = quill.getSelection(true);
                    quill.insertEmbed(range.index, 'html5video', videoUrl);
                    setAttachments(prev => [...prev, {
                        name: file.name,
                        fileName: storedFileName,
                        url: videoUrl,
                        type: file.type || 'application/octet-stream'
                    }]);
                }
            } catch (error) {
                console.error("Error uploading video:", error);
            }
        };
    };

    const fileHandler = () => {
        const input = document.createElement('input');
        input.setAttribute('type', 'file');
        input.setAttribute('accept', '.pdf, .csv, .doc, .docx, .xls, .xlsx, .txt');
        input.click();
        input.onchange = async () => {
            const file = input.files[0];
            if (!file) return;
            const formData = new FormData();
            formData.append('file', file);
            try {
                const response = await fetch(`${config.SUPPORT_ENDPOINT}upload`, { method: 'POST', body: formData });
                if (!response.ok) throw new Error('Upload failed');
                const data = await response.json();
                const fileUrl = config.SUPPORT_BASEURL + data.url || (data.fileName ? `${config.SUPPORT_ENDPOINT}uploads/${data.fileName}` : null);
                const storedFileName = data?.url ? data.url.split('/').filter(Boolean).pop() : data.fileName;

                if (fileUrl && storedFileName) {
                    // Add to attachments state instead of inserting into editor
                    setAttachments(prev => [...prev, {
                        name: file.name,
                        fileName: storedFileName,
                        url: fileUrl,
                        type: file.type || 'application/octet-stream'
                    }]);
                }
            } catch (error) {
                console.error("Error uploading file:", error);
            }
        };
    };

    const removeAttachment = (index) => {
        const attachmentToRemove = attachments[index];
        setAttachments(prev => prev.filter((_, i) => i !== index));

        if (quillRef.current && attachmentToRemove) {
            const quill = quillRef.current.getEditor();
            const delta = quill.getContents();
            let currentPos = 0;

            for (const op of delta.ops) {
                if (op.insert) {
                    const isObject = typeof op.insert === 'object';
                    const length = isObject ? 1 : op.insert.length;

                    if (isObject) {
                        if ((op.insert.image === attachmentToRemove.url) || (op.insert.html5video === attachmentToRemove.url)) {
                            quill.deleteText(currentPos, 1);
                            return;
                        }
                    }
                    currentPos += length;
                }
            }
        }
    };

    const modules = useMemo(() => ({
        magicUrl: true,
        keyboard: {
            bindings: {
                'list autofill': {
                    key: ' ',
                    collapsed: true,
                    format: { list: false },
                    prefix: /^\s*(\d+)\.$/,
                    handler: function (range, context) {
                        const match = context.prefix.match(/^\s*(\d+)\.$/);
                        if (match) {
                            this.quill.insertText(range.index, ' ');
                            return false;
                        }
                        return true;
                    }
                }
            }
        },
        // [FIX] Override default uploader to prevent local base64 insertion
        uploader: {
            handler: (range, files) => {
                const filesArray = Array.from(files);
                filesArray.forEach(file => {
                    // Check if it's an image
                    if (file.type.startsWith('image/')) {
                        uploadImage(file);
                    } else if (file.type.startsWith('video/')) {
                        // Optional: Handle video drop if supported
                    }
                });
            }
        },
        toolbar: {
            container: [
                [{ 'header': [1, 2, 3, 4, 5, false] }],
                ['bold', 'italic', 'underline', 'strike', 'blockquote'],
                [{ 'list': 'bullet' }, { 'indent': '-1' }, { 'indent': '+1' }],
                ['image', 'video', 'file', 'link'],
            ],
            handlers: {
                image: imageHandler,
                video: videoHandler,
                file: fileHandler
            }
        }
    }), []);

    const formats = [
        'header', 'bold', 'italic', 'underline', 'strike', 'blockquote',
        'list', 'bullet', 'indent', 'link', 'image', 'video', 'html5video'
    ];

    const handleCloseCreateModal = () => {
        setShowCreateModal(false);
        setCreateFormData({ name: '', to: '', subject: '', description: '' });
        setAttachments([]);
        setCcValues([]);
        setCcInputValue(""); // Clear CC Input
        setShowCC(false);
        setConversationId(null);
        setDraftTicketId(null);
        setFormErrors({});
    };

    const handlePreCreate = () => {
        const errors = {};
        if (!createFormData.name) {
            errors.name = "Name is required";
        }
        if (!createFormData.subject) {
            errors.subject = "Subject is required";
        }
        if (!createFormData.description || createFormData.description.replace(/<[^>]*>/g, '').trim() === '') {
            errors.description = "Description is required";
        }

        if (Object.keys(errors).length > 0) {
            setFormErrors(errors);
            return;
        }

        setShowCreateConfirmModal(true);
    };

    const confirmCreate = async () => {
        setIsCreating(true);
        const crmUser = JSON.parse(localStorage.getItem('crmUser'));
        const token = crmUser?.token || localStorage.getItem('token');

        try {
            const payload = {
                receiveName: createFormData.name,
                receiveEmail: createFormData.to || "",
                title: createFormData.subject,
                description: createFormData.description,
                attachments: attachments.map(att => att.fileName),
                cc: ccValues.map(c => c.email || c.value),
                conversationId: conversationId,
                user: {
                    full_name: crmUser?.full_name,
                    email_address: crmUser?.email_address
                }
            };


            const response = await fetch(`${config.SUPPORT_ENDPOINT}tickets`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });
            const result = await response.json();
            if (result.success) {
                setShowCreateModal(false);
                setShowCreateConfirmModal(false);
                setCreateFormData({ name: '', to: '', subject: '', description: '' });
                setAttachments([]);
                setCcValues([]);
                setCcInputValue(""); // Clear CC Input
                setShowCC(false);
                setConversationId(null);
                setFormErrors({});
                navigate(`/ticket/${result.data.id}`);
            } else {
                alert("Failed to create ticket");
            }
        } catch (error) {
            console.error("Error creating ticket:", error);
            alert("Error creating ticket");
        }
        setIsCreating(false);
    };

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(tableSearch);
        }, 500);
        return () => clearTimeout(timer);
    }, [tableSearch]);

    useEffect(() => {
        if (tableFilters?.ticketStatus) {
            localStorage.setItem(SUPPORT_TAB_STORAGE_KEY, tableFilters.ticketStatus);
        }
    }, [tableFilters?.ticketStatus]);

    useEffect(() => {
        getInvoiceRequests();
    }, [pagination.page, pagination.limit, tableFilters, debouncedSearch, sortConfig, selectedStatusFilter, selectedAgentFilter]);

    const getInvoiceRequests = async () => {
        setIsLoadingPage(true);
        const crmUser = JSON.parse(localStorage.getItem('crmUser'));
        const myEmail = crmUser?.email_address;

        let url = '';
        const searchQuery = debouncedSearch ? `&search=${encodeURIComponent(debouncedSearch)}` : '';

        const ticketStatus = tableFilters?.ticketStatus;
        let statusQuery = '';

        if (myEmail) {
            statusQuery += `&myEmail=${encodeURIComponent(myEmail)}`;
        }

        if (ticketStatus === 'openTicket') {
            statusQuery += '&openTicket=true';
        } else if (ticketStatus === 'pending') {
            statusQuery += '&status=PENDING';
        } else if (ticketStatus === 'closed') {
            statusQuery += '&status=CLOSED';
        } else if (ticketStatus === 'myTicket') {
            if (myEmail) {
                statusQuery += `&userEmail=${encodeURIComponent(myEmail)}`;
            }
        } else if (ticketStatus === 'watchList') {
            statusQuery += '&isWatch=true';
        } else if (ticketStatus === 'unassigned') {
            statusQuery += '&unassigned=true';
        }

        if (selectedStatusFilter && selectedStatusFilter.value !== '') {
            // override the tab status query if explicitly filtered
            statusQuery += `&status=${selectedStatusFilter.value}`;
        }

        if (selectedAgentFilter && selectedAgentFilter.value !== 'ALL') {
            if (selectedAgentFilter.value === '') {
                // Unassigned logic
                statusQuery += '&unassigned=true';
            } else if (selectedAgentFilter.email) {
                // use userEmail based on selected agent filter email
                statusQuery += `&userEmail=${encodeURIComponent(selectedAgentFilter.email)}`;
            }
        }

        // Add Sorting
        const sortQuery = `&sortBy=${sortConfig.key}&sortOrder=${sortConfig.direction}`;

        url = `${config.SUPPORT_ENDPOINT}tickets?page=${pagination.page}&limit=${pagination.limit}${statusQuery}${searchQuery}${sortQuery}`;

        try {
            const response = await jwtFetch(url);
            const result = await response.json();
            if (result.success) {
                setInvoiceRequests(result.data);
                setOpenTicketCount(result.meta.openTicket || 0);
                setPendingTicketCount(result.meta.pending || 0);
                setClosedTicketCount(result.meta.closed || 0);
                setMyTicketCount(result.meta.myTicket || 0);
                setWatchListCount(result.meta.watchList || 0);
                setUnassignedCount(result.meta.unassigned || 0);
                setPagination(prev => ({
                    ...prev,
                    total: result.meta.total,
                    totalPages: result.meta.totalPages,
                    page: result.meta.page,
                    limit: result.meta.limit
                }));
            } else {
                alert('Failed to fetch data');
            }
        } catch (error) {
            console.log(error)
            console.error('Error fetching data:', error);
            alert('Error fetching data');
        }
        setIsLoadingPage(false);
    }

    const handleDeleteTicket = async (id, e) => {
        e.stopPropagation(); // Prevent navigating to ticket detail
        if (window.confirm('Are you sure you want to delete this ticket and all its attachments?')) {
            try {
                const response = await fetch(`${config.SUPPORT_ENDPOINT}tickets/${id}`, {
                    method: 'DELETE',
                });
                const result = await response.json();
                if (result.success) {
                    getInvoiceRequests();
                } else {
                    alert('Failed to delete ticket');
                }
            } catch (error) {
                console.error('Error deleting ticket:', error);
                alert('Error deleting ticket');
            }
        }
    };

    const clearSearch = () => {
        setTableSearch('');
        if (searchInputRef.current) {
            searchInputRef.current.focus();
        }
    }

    const getStatusColor = (status) => {
        switch (status) {
            case 'NEW': return '#1b8756';
            case 'IN_PROGRESS': return '#0d6efd';
            case 'ON_HOLD': return '#6c757d';
            case 'SOLVED': return '#93ace6';
            case 'CLOSED': return '#8410DCFF';
            case 'INFORMATION_REQUESTED': return '#ffc105';
            default: return '#6c757d';
        }
    };

    const getPriorityColor = (priority) => {
        switch (priority) {
            case 'EMERGENCY': return '#dc3545';
            case 'URGENT': return '#fd7e14';
            case 'NORMAL': return '#0d6efd';
            case 'LOW': return '#6c757d';
            default: return '#0d6efd';
        }
    };

    const handleSort = (key) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    const SortableHeader = ({ label, sortKey, width }) => {
        const isActive = sortConfig.key === sortKey;
        return (
            <th
                className={`bg-gold ${width} user-select-none text-center`}
                style={{ cursor: 'pointer', position: 'relative' }}
                onClick={() => handleSort(sortKey)}
            >
                <div className="d-flex align-items-center justify-content-center">
                    <span>{label}</span>
                    {isActive && (
                        <span className="d-flex flex-column lh-1 ms-2" style={{ fontSize: '10px' }}>
                            {sortConfig.direction === 'asc' ? (
                                <i className="bi bi-sort-up-alt text-dark" />
                            ) : (
                                <i className="bi bi-sort-down-alt text-dark" />
                            )}
                        </span>
                    )}
                </div>
            </th>
        );
    };

    const truncateText = (value, maxLength = 45) => {
        if (!value || typeof value !== "string") return value || "";
        if (value.length <= maxLength) return value;
        return `${value.slice(0, maxLength).trim()}...`;
    };

    const renderPaginationItems = () => {
        const { page, totalPages } = pagination;
        const items = [];

        if (totalPages <= 7) {
            for (let i = 1; i <= totalPages; i++) {
                items.push(i);
            }
        } else {
            if (page <= 4) {
                for (let i = 1; i <= 5; i++) items.push(i);
                items.push('...');
                items.push(totalPages);
            } else if (page >= totalPages - 3) {
                items.push(1);
                items.push('...');
                for (let i = totalPages - 4; i <= totalPages; i++) items.push(i);
            } else {
                items.push(1);
                items.push('...');
                items.push(page - 1);
                items.push(page);
                items.push(page + 1);
                items.push('...');
                items.push(totalPages);
            }
        }

        return items.map((item, index) => (
            <li key={index} className="page-item">
                {item === '...' ? (
                    <span className="page-link border-0 text-secondary bg-transparent" style={{ width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>...</span>
                ) : (
                    <button
                        className={`page-link border-0 rounded-circle d-flex align-items-center justify-content-center ${page === item ? 'bg-gold text-white shadow-sm' : 'text-secondary bg-transparent'}`}
                        style={{ width: '32px', height: '32px' }}
                        onClick={() => setPagination(prev => ({ ...prev, page: item }))}
                    >
                        {item}
                    </button>
                )}
            </li>
        ));
    };

    return (
        <div id="invoicerequest-list">
            <div className="container-fluid p-0">
                <div className="row g-0 mh-100vh">
                    <div className="col-md-2 border-end layout-sticky-sidebar">
                        <div className="d-md-flex d-none align-items-center justify-content-center p-1 h-48">
                            <a className="h-100" href="/dashboard"><img className="h-100" src="/cuancuan.png" alt="PointStar" /></a>
                        </div>
                        <hr className="m-0 d-none d-md-block" />
                        <Menu active="support" />
                    </div>
                    <div className="col-md-10 layout-scroll-column">
                        <div className="layout-sticky-header">
                            <Header />
                            <hr className="m-0" />
                        </div>
                        <div className="main">
                            <div className="p-3 p-md-4">
                                <section className="mb-4">
                                    <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between mb-3 gap-2">
                                        <div className="fs-24 flex-shrink-0">Ticket List</div>
                                        <div className="d-flex flex-wrap align-items-center gap-2">
                                            <div style={{ width: '150px' }}>
                                                <Select
                                                    options={statusOptions}
                                                    value={selectedStatusFilter}
                                                    onChange={(selected) => setSelectedStatusFilter(selected || statusOptions[0])}
                                                    placeholder="Status"
                                                    isClearable={true}
                                                />
                                            </div>
                                            <div style={{ width: '150px' }}>
                                                <Select
                                                    options={agentOptions}
                                                    value={selectedAgentFilter}
                                                    onChange={(selected) => setSelectedAgentFilter(selected || agentOptions[0])}
                                                    placeholder="Agent"
                                                    isClearable={true}
                                                />
                                            </div>
                                            <div className="input-group input-search" style={{ minWidth: '160px', flex: '1 1 160px' }}>
                                                <button className="btn bg-white" type="button" onClick={() => clearSearch()}>{tableSearch ? <i className="bi bi-x-lg" /> : <i className="bi bi-search" />}</button>
                                                <input className="form-control" type="text" placeholder="Search" value={tableSearch} onChange={(e) => setTableSearch(e.target.value)} ref={searchInputRef} autoFocus />
                                            </div>
                                            {crmUser.user_level !== 'View' && (
                                                <button className="btn btn-primary bg-gold text-dark border-0 flex-shrink-0" type="button" onClick={() => {
                                                    setShowCreateModal(true);
                                                    setConversationId(uuidv4());
                                                }}><i className="bi bi-plus-circle me-1" /> Add</button>
                                            )}
                                        </div>
                                    </div>
                                    <div className="bg-white border-radius-4 p-2 p-md-4">
                                        <div style={{ overflowX: 'auto' }}>
                                            <Tabs
                                                className="mb-3 flex-nowrap"
                                                activeKey={tableFilters?.ticketStatus || "myTicket"}
                                                onSelect={(key) => {
                                                    setTableFilters({ ticketStatus: key });
                                                    setPagination(prev => ({ ...prev, page: 1 }));
                                                    setSortConfig({ key: 'updatedAt', direction: 'desc' });
                                                }}
                                            >
                                                <Tab eventKey="myTicket" title={`My Ticket (${myTicketCount})`} />
                                                <Tab eventKey="watchList" title={`Watch List (${watchListCount})`} />
                                                <Tab eventKey="unassigned" title={`Unassigned (${unassignedCount})`} />
                                                <Tab eventKey="openTicket" title={`Open Ticket (${openTicketCount})`} />
                                                <Tab eventKey="pending" title={`Pending (${pendingTicketCount})`} />
                                                <Tab eventKey="closed" title={`Closed Ticket (${closedTicketCount})`} />
                                            </Tabs>
                                        </div>
                                        {isLoadingPage ? (
                                            <p>Loading...</p>
                                        ) : (
                                            invoiceRequests && invoiceRequests.length > 0 ? (
                                                <>
                                                    <div className="table-responsive">
                                                        <table className="table table-hover">
                                                            <thead>
                                                                <tr>
                                                                    <th className={"bg-gold w-40"}>#</th>
                                                                    <SortableHeader label="ID" sortKey="code" width="w-300" />
                                                                    <SortableHeader label="Title" sortKey="title" width="w-300" />
                                                                    <SortableHeader label="Sender" sortKey="receiveName" width="w-200" />
                                                                    <SortableHeader label="Status" sortKey="status" width="w-120" />
                                                                    <SortableHeader label="Agent" sortKey="userName" width="w-120" />
                                                                    <SortableHeader label="Resolution" sortKey="resolution" width="w-120" />
                                                                    <SortableHeader label="Queue" sortKey="queueId" width="w-120" />
                                                                    <SortableHeader label="Priority" sortKey="priority" width="w-120" />
                                                                    <SortableHeader label="Created At" sortKey="createdAt" width="w-160" />
                                                                    <SortableHeader label="Updated At" sortKey="updatedAt" width="w-160" />
                                                                    {crmUser?.user_role_division === "Manager" && (
                                                                        <th className="bg-gold w-40 text-center">Action</th>
                                                                    )}
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {invoiceRequests.map((item, index) => (
                                                                    <tr
                                                                        key={item.id}
                                                                        style={{ cursor: 'pointer' }}
                                                                        onClick={() => navigate(`/ticket/${item.id}`)}
                                                                        onContextMenu={(e) => {
                                                                            e.preventDefault();
                                                                            window.open(`/ticket/${item.id}`, '_blank');
                                                                        }}
                                                                    >
                                                                        <td valign="middle" align="center">{((pagination.page - 1) * pagination.limit) + index + 1}</td>
                                                                        <td valign="middle" className="text-center"><strong>{item.code}</strong></td>
                                                                        <td valign="middle" title={item.title || ''}><strong>{truncateText(item.title)}</strong></td>
                                                                        <td valign="middle">
                                                                            <div>{item.receiveName || 'Unknown'}</div>
                                                                            <div className="text-muted small">{item.receiveEmail || 'Unknown'}</div>
                                                                        </td>
                                                                        <td valign="middle" className="text-center"><span className={`badge ${item?.status === "INFORMATION_REQUESTED" ? "text-dark" : "text-white"}`} style={{ backgroundColor: getStatusColor(item?.status) }}>{item?.status?.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}</span></td>
                                                                        <td valign="middle" className="text-center">{item?.userName || '-'} </td>
                                                                        <td valign="middle" className="text-center">{item.resolution}</td>
                                                                        <td valign="middle" className="text-center">{item.queueId ? item.queue?.name || '-' : '-'}</td>
                                                                        <td valign="middle" className="text-center"> <span className="badge" style={{ backgroundColor: getPriorityColor(item?.priority) }}>{item?.priority?.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}</span></td>
                                                                        <td valign="middle" className="text-center">{format(new Date(item.createdAt), 'd MMM yyyy HH:mm')}</td>
                                                                        <td valign="middle" className="text-center">{format(new Date(item.updatedAt), 'd MMM yyyy HH:mm')}</td>
                                                                        {crmUser?.user_role_division === "Manager" && (
                                                                            <td valign="middle" className="text-center">
                                                                                <button className="btn btn-sm btn-danger" onClick={(e) => handleDeleteTicket(item.id, e)} title="Delete Ticket">
                                                                                    <i className="bi bi-trash"></i>
                                                                                </button>
                                                                            </td>
                                                                        )}
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                    {pagination.totalPages > 1 && (
                                                        <div className="d-flex justify-content-between align-items-center mt-3">
                                                            <div className="d-flex align-items-center gap-2 text-secondary">
                                                                <small>Show</small>
                                                                <select className="form-select form-select-sm border-secondary text-secondary" style={{ width: '70px', borderRadius: '4px' }} value={pagination.limit} onChange={(e) => setPagination(prev => ({ ...prev, limit: Number(e.target.value), page: 1 }))}>
                                                                    <option value={10}>10</option>
                                                                    <option value={20}>20</option>
                                                                    <option value={50}>50</option>
                                                                </select>
                                                                <small>entries</small>
                                                            </div>
                                                            <nav>
                                                                <ul className="pagination pagination-sm mb-0 gap-1">
                                                                    <li className={`page-item ${pagination.page === 1 ? 'disabled' : ''}`}>
                                                                        <button className="page-link border-0 text-secondary bg-transparent" onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))} disabled={pagination.page === 1}>
                                                                            <i className="bi bi-chevron-left" />
                                                                        </button>
                                                                    </li>
                                                                    {renderPaginationItems()}
                                                                    <li className={`page-item ${pagination.page === pagination.totalPages ? 'disabled' : ''}`}>
                                                                        <button className="page-link border-0 text-secondary bg-transparent" onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))} disabled={pagination.page === pagination.totalPages}>
                                                                            <i className="bi bi-chevron-right" />
                                                                        </button>
                                                                    </li>
                                                                </ul>
                                                            </nav>
                                                        </div>
                                                    )}
                                                </>
                                            ) : (
                                                <div className="text-center p-4">No data available</div>
                                            )
                                        )}
                                    </div>
                                </section>

                            </div>
                        </div>
                    </div>
                </div>
            </div >
            <Modal show={showCreateModal} onHide={handleCloseCreateModal} size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title>Create New Ticket</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label>Name</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Enter name"
                                value={createFormData.name}
                                onChange={(e) => {
                                    setCreateFormData(prev => ({ ...prev, name: e.target.value }));
                                    if (formErrors.name) setFormErrors(prev => ({ ...prev, name: '' }));
                                }}
                                isInvalid={!!formErrors.name}
                            />
                            <Form.Control.Feedback type="invalid">
                                {formErrors.name}
                            </Form.Control.Feedback>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>To</Form.Label>
                            <div className="d-flex align-items-center gap-2">
                                <Form.Control
                                    type="email"
                                    placeholder="Enter recipient email"
                                    value={createFormData.to}
                                    onChange={(e) => setCreateFormData(prev => ({ ...prev, to: e.target.value }))}
                                />
                                <button
                                    type="button"
                                    className="btn btn-link text-decoration-none"
                                    onClick={() => setShowCC(!showCC)}
                                >
                                    CC
                                </button>
                            </div>
                        </Form.Group>

                        {showCC && (
                            <Form.Group className="mb-3">
                                <Form.Label>CC</Form.Label>
                                <CreatableSelect
                                    isMulti
                                    options={ccOptions}
                                    value={ccValues}
                                    inputValue={ccInputValue}
                                    onInputChange={(val, meta) => handleCcInputChange(val, meta)}
                                    onKeyDown={handleCcKeyDown}
                                    onChange={(val) => setCcValues(val)}
                                    placeholder="Type or select CCs..."
                                    components={{
                                        MultiValue: CcMultiValue,
                                        Option: CcOption
                                    }}
                                    styles={{
                                        control: (base) => ({
                                            ...base,
                                            fontSize: '14px'
                                        }),
                                        menu: (base) => ({
                                            ...base,
                                            zIndex: 9999
                                        })
                                    }}
                                />
                            </Form.Group>
                        )}

                        <Form.Group className="mb-3">
                            <Form.Label>Subject</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Enter subject"
                                value={createFormData.subject}
                                onChange={(e) => {
                                    setCreateFormData(prev => ({ ...prev, subject: e.target.value }));
                                    if (formErrors.subject) setFormErrors(prev => ({ ...prev, subject: '' }));
                                }}
                                isInvalid={!!formErrors.subject}
                            />
                            <Form.Control.Feedback type="invalid">
                                {formErrors.subject}
                            </Form.Control.Feedback>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Description</Form.Label>
                            <div style={{ height: '300px', marginBottom: '50px' }}>
                                <ReactQuill
                                    ref={quillRef}
                                    theme="snow"
                                    value={createFormData.description}
                                    onChange={(value) => {
                                        setCreateFormData(prev => ({ ...prev, description: value }));
                                        const cleanText = value.replace(/<[^>]*>/g, '').trim();
                                        if (cleanText && formErrors.description) {
                                            setFormErrors(prev => ({ ...prev, description: '' }));
                                        }
                                    }}
                                    modules={modules}
                                    formats={formats}
                                    style={{ height: '100%' }}
                                />
                            </div>
                            {formErrors.description && <div className="text-danger small mt-1">{formErrors.description}</div>}
                            {/* Attachment Chips */}
                            <div className="d-flex flex-wrap gap-2 mt-2">
                                {attachments.map((att, index) => (
                                    <div key={index} className="badge bg-light text-dark border d-flex align-items-center p-2 rounded-pill">
                                        <i className={`bi ${att.type?.startsWith('image/') ? 'bi-image' : att.type?.startsWith('video/') ? 'bi-camera-video' : 'bi-file-earmark-text'} me-2`}></i>
                                        <span className="me-2" style={{ maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {att.name}
                                        </span>
                                        <i className="bi bi-x-circle-fill text-danger cursor-pointer" onClick={() => removeAttachment(index)} style={{ cursor: 'pointer' }}></i>
                                    </div>
                                ))}
                            </div>
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseCreateModal}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={handlePreCreate} disabled={isCreating}>
                        {isCreating ? 'Creating...' : 'Create Ticket'}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Confirmation Modal for Create Ticket */}
            <Modal show={showCreateConfirmModal} onHide={() => setShowCreateConfirmModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title className="fw-bold">Confirm Ticket Creation</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="text-center mb-3">
                        <i className="bi bi-ticket-perforated-fill text-primary display-1"></i>
                    </div>
                    <p className="text-center fs-5">
                        Are you sure you want to create this ticket?
                    </p>
                    <div className="alert alert-info d-flex align-items-center" role="alert">
                        <i className="bi bi-info-circle-fill me-2"></i>
                        <div>
                            This will create a new ticket and notify the assigned agents.
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowCreateConfirmModal(false)} disabled={isCreating}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={confirmCreate} disabled={isCreating}>
                        {isCreating ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                Creating...
                            </>
                        ) : (
                            'Yes, Create Ticket'
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div >
    );
};

export default Ticket;




